import React, { createContext } from 'react'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import IShelterTax from '../../interfaces/IShelterTax'
import {
  SearchRequest,
  ShelterTaxSaveRequest,
  ShelterTaxDeleteRequest
} from '../../services/api/interfaces/ApiRequest'
import {
  shelterTaxList,
  shelterTaxCreate,
  shelterTaxUpdate,
  shelterTaxDelete
} from '../../services/api'

interface ShelterTaxContextState extends BaseContextState {
  taxes: Array<IShelterTax> | null,
  tax: IShelterTax | null,
}

interface ShelterTaxContextData extends ShelterTaxContextState {
  resetErrors: Function,
  requestShelterTaxList: (args: SearchRequest) => void,
  requestShelterTaxFind: (args: SearchRequest) => void,
  requestShelterTaxCreate: (args: ShelterTaxSaveRequest) => Promise<any>,
  requestShelterTaxUpdate: (args: ShelterTaxSaveRequest) => Promise<any>,
  requestShelterTaxDelete: (args: ShelterTaxDeleteRequest) => Promise<any>
}

// Create context
const { Provider, Consumer } = createContext({} as ShelterTaxContextData)

// Export consumer
export const ShelterTaxConsumer = Consumer

export class ShelterTaxProvider extends BaseContextProvider {
  state: ShelterTaxContextState = {
    taxes: null,
    tax: null
  }

  lastListPayload: SearchRequest | null = null

  /**
   * Make api request for shelter tax list
   * 
   * @param {SearchRequest} payload 
   */
  requestShelterTaxList = (payload: SearchRequest) => {
    this.setState({
      fetching: true,
      error: null
    }, async () => {
      this.lastListPayload = payload
      const response = await shelterTaxList(payload)

      // Process our response
      this.processResponse(response, { 
        paginate: {
          destination: 'taxes'
        },
      })
    })
  }

  /**
   * Make api request for shelter tax finder
   * 
   * @param {SearchRequest} payload 
   */
  requestShelterTaxFind = (payload: SearchRequest) => {
    this.setState({
      fetching: true,
      error: null,
      tax: null
    }, async () => {
      payload.options = {
        ...payload.options,
        _single: true
      }
      const response = await shelterTaxList(payload)

      // Process our response
      this.processResponse(response, { 
        mappings: {
          destination: 'tax'
        }
      })
    })
  }

  /**
   * Make api request for shelter tax create
   * 
   * @param {ShelterTaxSaveRequest} payload 
   */
  requestShelterTaxCreate = (payload: ShelterTaxSaveRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null,
        tax: null
      }, async () => {
        let response = await shelterTaxCreate(payload)
        this.processResponse(response, {
          keepFetching: true
        })

        if (this.state.success) {
          await this.requestShelterTaxFind({
            search: {
              id: response.data.data?.organization_tax.id,
              'tax_branch': {
                with: {
                  deleted: false,
                  branch: {
                    with: true
                  }
                }
              }
            },
            options: {
              _force_ids: true
            }
          })
          resolve(true)
        } else {
          this.setState({
            fetching: false
          }, () => {
            reject(this.state.error)
          })
        }
      })
    })
  }

  /**
   * Make api request for shelter tax update
   * 
   * @param {ShelterTaxSaveRequest} payload 
   */
  requestShelterTaxUpdate = (payload: ShelterTaxSaveRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await shelterTaxUpdate(payload)
        this.processResponse(response, {
          mappings: [
            { origin: 'organization_tax', destination: 'tax' }
          ]
        })
  
        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for shelter tax delete
   * 
   * @param {ShelterTaxDeleteRequest} payload 
   */
  requestShelterTaxDelete = (payload: ShelterTaxDeleteRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await shelterTaxDelete(payload)
        this.processResponse(response)
  
        if (this.state.success) {
          this.requestShelterTaxList(this.lastListPayload || {})
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          requestShelterTaxList: this.requestShelterTaxList,
          requestShelterTaxFind: this.requestShelterTaxFind,
          requestShelterTaxCreate: this.requestShelterTaxCreate,
          requestShelterTaxUpdate: this.requestShelterTaxUpdate,
          requestShelterTaxDelete: this.requestShelterTaxDelete
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}
